import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLock, IconRotateClockwise } from '@tabler/icons-react';
import { layerBox } from '../../../../model/geometry';
import { handleCursor, handlesFor } from '../transform';

// Places a box element over a layer, in screen pixels (document pixels × scale).
export function placeBox(element, layer, scale) {
  if (!element) return;
  const box = layerBox(layer);
  element.style.left = `${box.x * scale}px`;
  element.style.top = `${box.y * scale}px`;
  element.style.width = `${box.width * scale}px`;
  element.style.height = `${box.height * scale}px`;
  element.style.transform = box.rotation ? `rotate(${box.rotation}deg)` : '';
}

// The selected layer's frame: resize handles at a fixed screen size and a rotation grip
// above it. While a gesture runs, the stage moves it imperatively (`place`), so dragging
// never renders React.
const SelectionBox = forwardRef(function SelectionBox({ layer, scale, readOnly, onHandleDown }, ref) {
  const { t } = useTranslation('artEditor');
  const element = useRef(null);
  useImperativeHandle(ref, () => ({ place: next => placeBox(element.current, next, scale), element: element.current }), [scale]);
  const box = layerBox(layer);
  const locked = layer.locked || readOnly;
  return <div ref={element} className={`art-selection ${locked ? 'is-locked' : ''}`} style={{ left: box.x * scale, top: box.y * scale, width: box.width * scale, height: box.height * scale,
    transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined }}>
    {layer.locked && <span className="art-selection__lock" aria-label={t('locked')}><IconLock aria-hidden="true" /></span>}
    {!locked && handlesFor(layer).map(handle => <span key={handle} className={`art-selection__handle is-${handle}`} style={{ cursor: handleCursor(handle, box.rotation) }}
      onPointerDown={event => onHandleDown(handle, event)} />)}
    {!locked && <span className="art-selection__rotate" role="presentation" title={t('rotate')} onPointerDown={event => onHandleDown('rotate', event)}>
      <IconRotateClockwise aria-hidden="true" />
    </span>}
  </div>;
});

export default SelectionBox;
